import { ArrowDownward } from "@mui/icons-material";
import { Box, Chip, CircularProgress, Typography, useTheme } from "@mui/material";

import { Timings } from "@storyboard/web-logger/timings/Timings";
import { createLogger } from "@/logger";

const log = createLogger("feed");

import { eq } from "drizzle-orm";
import { type Dispatch, Profiler, type SetStateAction, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import FeedItemHeader from "@/components/feed/classic/FeedItemHeader";
import FeedItem from "@/components/feed/FeedItem";
import {
  feedFooterHeight,
  footerTransitionSx,
  globalAudioPlayerHeight,
  topHeaderHeight,
} from "@/components/NewDesign/newDesignUtils.ts";
import VirtualizedList from "@/components/VirtualizedList.tsx";
import { db } from "@/db/db";
import { item as itemTable } from "@/db/schema";
import Locator from "@/locator.ts";
import { ActionContext } from "@/models/ActionsProvider";
import { AppContext } from "@/models/AppStateProvider.tsx";
import { AudioAppContext } from "@/models/AudioAppContextProvider.tsx";
import { fetchFeedItems } from "@/models/actions/feedItems";
import { downloadFeedItemContent } from "@/models/actions/initialFeedLoad";
import { DataContext } from "@/models/DataProvider";
import { FeedContext, type FullItem } from "@/models/FeedContextProvider";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { fetchFeedCommandAliasesQuery } from "@/models/repositories";
import { resetOrphanedLoadedContent } from "@/models/repositories/Item";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider.tsx";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { actions, TelemetryContext } from "@/models/TelemetryProvider";
import { TrackingContext } from "@/models/TrackingStateProvider";

const highlightTimeout = 5000;

function onRenderCallback(id: string, phase: string, actualDuration: number) {
  if (actualDuration > 5) {
    Timings.createSpan(`react-render:${id}`, performance.now() - actualDuration, performance.now(), {
      properties: { phase, duration: actualDuration },
    });
  }
}

export default function FeedVirtualizedList({
  hasGlobalAudioPlayer,
  currentFeedDate,
  setRenderToggle,
}: {
  hasGlobalAudioPlayer: boolean;
  currentFeedDate: Date;
  setRenderToggle: Dispatch<SetStateAction<string>>;
}) {
  const { feedMap, vListRef, currentFeed, expandFeedWindow, windowFullyLoaded, setFeedWindowStartTimestamp } =
    useContext(FeedContext);
  const { flags } = useContext(AppContext);
  const theme = useTheme();
  const { myAccount } = useContext(MyAccountContext);
  const { trackAction, finishAction } = useContext(TelemetryContext);
  const { preferredLanguage } = useContext(DataContext);
  const { currentWorkspaceId } = useContext(WorkspaceContext);
  const { accountEvent } = useContext(ActionContext);
  const { ampli } = useContext(TrackingContext);
  const location = useLocation();
  const navigate = useNavigate();
  const feedItemId = new URLSearchParams(location.search).get("feedItemId") ?? null;
  const hashValue = location.hash?.split("#")?.[1] ?? null;
  const hasContentId = hashValue || feedItemId;
  const [activeHashOrFeedItemId, _setActiveHashOrFeedItemId] = useState<string | null>(hasContentId);
  const activeHashOrFeedItemIdRef = useRef<string | null>(hasContentId);
  const setActiveHashOrFeedItemId = (value: string | null | (() => string | null)) => {
    const resolved = typeof value === "function" ? value() : value;
    activeHashOrFeedItemIdRef.current = resolved;
    _setActiveHashOrFeedItemId(resolved);
  };
  const [activeFeedItemIndex, setActiveFeedItemIndex] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [range, setRange] = useState<number[]>([0, 0]);
  const [showScrollToBottom, setShowScrollToBottom] = useState<boolean>(false);
  const [allowJumpButton, setAllowJumpButton] = useState<boolean>(false);
  const [preventScroll, setPreventScroll] = useState<boolean>(false);

  // Convert from useState to useRef
  const isLoadedFirstPageRef = useRef<boolean>(currentFeed?.loadedFirstPage || false);
  const isLoadedLastPageRef = useRef<boolean>(currentFeed?.loadedLastPage || false);
  const isLoadingItemsRef = useRef<boolean>(false);
  const initialItemLoadRef = useRef<boolean>(true);

  const feedMapRef = useRef(feedMap);
  feedMapRef.current = feedMap;

  // Track fetched timestamp ranges to avoid re-fetching
  const oldestFetchedTimestampRef = useRef<string | null>(null);
  const newestFetchedTimestampRef = useRef<string | null>(null);

  const initialContentLoad = useRef<boolean>(true);
  const downloadedContentIds = useRef(new Set<string>());
  const [fetchDoneFeedId, setFetchDoneFeedId] = useState<string | null>(null);
  const countThreshold = 5;
  const feedId = currentFeed?.id;
  const currentFeedItems = feedMap?.[0]?.feedId === feedId ? feedMap : [];
  const fetchDone = fetchDoneFeedId === feedId;
  const workspaceId = currentWorkspaceId;
  const pageSize = 50;

  const aliasChannelDetails =
    useReactiveSelectQuery(
      fetchFeedCommandAliasesQuery,
      [currentFeed?.id],
      "FeedVirtualizedList:fetchFeedCommandAliasesQuery",
    ) ?? [];

  const aliasChannelOwnerId = aliasChannelDetails?.[0]?.id ?? null;

  const feedMapIds = useMemo(
    () => feedMap?.filter((feedItem) => feedItem?.id !== feedId)?.map((feedItem) => feedItem.id),
    [feedMap, feedId],
  );

  const firstNewItem = useMemo(() => {
    const newCount = feedMap
      ?.filter((item) => item.accountId !== myAccount?.id)
      ?.map((item) => ({ id: item.id, createdAt: item.createdAt }))
      .filter((item) => {
        const dateCheck = new Date(item.createdAt).getTime() - new Date(currentFeedDate).getTime();
        return dateCheck > 0;
      });
    return feedMap?.[feedMap?.length - newCount?.length];
  }, [feedMap, currentFeedDate]);

  useEffect(() => {
    if (firstNewItem?.id) {
      const findFeedMapRefIndex = feedMapRef.current.findIndex((item) => item.id === firstNewItem.id);
      if (findFeedMapRefIndex > -1) {
        setActiveIndex(() => findFeedMapRefIndex);
      }
    }
  }, [firstNewItem]);

  const removeHash = (hashId: string) => {
    const hash = window.location.hash;
    const search = window.location.search?.replace("?feedItemId=", "");
    const hasHashValue = hash?.length > 0 ? hash?.replace("#", "") : search?.length > 0 ? search : null;

    if (!hasHashValue || hasHashValue?.length === 0 || hasHashValue !== hashId) {
      return;
    }

    if (window.location.hash || window.location.search?.includes("?feedItemId=")) {
      navigate(window.location.pathname, { replace: true });
      setActiveFeedItemIndex(() => null);
    }
    setActiveHashOrFeedItemId(() => null);
  };

  const setPreventScrollAction = () => {
    setPreventScroll(() => true);
    setTimeout(() => {
      setPreventScroll(() => false);
    }, 500);
  };

  // Phase 1: Fetch feed items based on pagination
  const fetchFeedItemsPage = async ({
    direction,
    referenceTimestamp,
    referenceItemId,
  }: {
    direction: "newer" | "older";
    referenceTimestamp?: string;
    referenceItemId?: string;
  }) => {
    isLoadingItemsRef.current = true;

    try {
      const spanName = initialContentLoad.current ? actions.feedInitial(currentFeed) : actions.feedScroll(currentFeed);
      trackAction(spanName, {
        feedId,
        direction,
        referenceTimestamp: referenceTimestamp || "none",
        referenceItemId: referenceItemId || "none",
      });

      log.debug("[FETCH ITEMS] fetchFeedItemsPage", {
        direction,
        referenceTimestamp,
        referenceItemId,
        items: feedMapRef.current?.length,
      });

      const result = await fetchFeedItems({
        workspaceId,
        feedId,
        direction,
        pageSize,
        referenceTimestamp,
        referenceItemId,
      });

      log.debug("[FETCH ITEMS] fetched feed items", {
        direction,
        referenceTimestamp,
        referenceItemId,
        result,
      });

      // Update pagination state based on results
      if (direction === "newer" && result.items.length < pageSize) {
        isLoadedLastPageRef.current = true;
      } else if (direction === "older" && result.items.length < pageSize) {
        isLoadedFirstPageRef.current = true;
        if (!referenceTimestamp && !referenceItemId) {
          isLoadedLastPageRef.current = true;
        }
      }

      // Scroll to newest items if this is the initial load and no specific item ID
      if (initialItemLoadRef.current && direction === "newer" && !activeHashOrFeedItemIdRef.current) {
        // Defer scrolling until after re-render
        setTimeout(() => {
          scrollToIndex(feedMapRef.current?.length - 1);
        }, 100);
      }

      initialItemLoadRef.current = false;
      setRenderToggle(feedId);
      finishAction(spanName);
    } catch (error) {
      console.error("Error fetching feed items:", error);
    } finally {
      isLoadingItemsRef.current = false;
    }
  };

  // Phase 2: Download content for visible items
  const downloadFeedItems = async () => {
    const isRangeUninitialized = range[0] === 0 && range[1] === 0 && feedMapRef.current?.length > 0;
    const [effectiveLower, effectiveUpper] = isRangeUninitialized
      ? [Math.max(0, feedMapRef.current.length - 50), feedMapRef.current.length]
      : range;
    const lowerBound = Math.max(0, effectiveLower - 20);
    const upperBound = Math.min(feedMapRef.current?.length, effectiveUpper + 20);
    const feedMapRange = feedMapRef.current.slice(lowerBound, upperBound);

    const recentCutoff = Date.now() - 5 * 60 * 1000;
    const needsContentDownload = (item: FullItem) => {
      if (!item.loadedContent) return true;
      const isRecent = new Date(item.createdAt).getTime() > recentCutoff;
      if (!isRecent) return false;
      return (
        !item.files?.length &&
        !item.audioEncodings?.length &&
        !item.transcriptions?.length &&
        !item.links?.length &&
        !item.callRecords?.length &&
        !item.displayArtifact
      );
    };
    const idMap = feedMapRange
      .filter((item) => needsContentDownload(item) && !downloadedContentIds.current.has(item.id))
      .map((item) => item.id)
      .filter(Boolean);

    const spanName = initialContentLoad.current ? actions.feedInitial(currentFeed) : actions.feedScroll(currentFeed);

    if (idMap.length > 0) {
      log.info("[ON-DEMAND CONTENT] Downloading content for visible items", {
        feedId,
        feedName: currentFeed?.title,
        itemCount: idMap.length,
        itemIds: idMap,
        isInitial: initialContentLoad.current,
        range: { lower: lowerBound, upper: upperBound },
      });

      trackAction(spanName, {
        feedId,
        feedName: currentFeed?.title,
        "feed.itemsLoaded": idMap.length.toString(),
      });
      const isInitialLoad = initialContentLoad.current;
      initialContentLoad.current = false;

      await downloadFeedItemContent(workspaceId, idMap, preferredLanguage);
      for (const id of idMap) downloadedContentIds.current.add(id);
      setRenderToggle(feedId);
      finishAction(spanName);

      Timings.createEvent("feed-content-painted", {
        properties: { feedId, feedName: currentFeed?.title, itemCount: idMap.length },
      });

      log.info("[ON-DEMAND CONTENT] Completed downloading content", {
        feedId,
        feedName: currentFeed?.title,
        itemCount: idMap.length,
      });

      if (isInitialLoad && !activeHashOrFeedItemIdRef.current) {
        setTimeout(() => {
          scrollToIndex(feedMapRef.current?.length - 1);
        }, 150);
      }
    }
  };

  // Check if we need to load more items when approaching the edges of our loaded set
  const checkForMoreItems = async () => {
    if (isLoadingItemsRef.current) {
      log.debug("[FETCH ITEMS] checkForMoreItems - already loading, skipping");
      return;
    }
    if (initialItemLoadRef.current) {
      log.debug("[FETCH ITEMS] checkForMoreItems - initial load in progress, skipping");
      return;
    }

    // Check if we need newer items
    if (range[0] <= 10 && !isLoadedLastPageRef.current) {
      const newestItemTimestamp = feedMapRef.current[0]?.createdAt;
      // Only fetch if this is a new range we haven't fetched before
      if (!newestFetchedTimestampRef.current || newestItemTimestamp !== newestFetchedTimestampRef.current) {
        log.debug("[FETCH ITEMS] checkForMoreItems - fetching newer", {
          newestItemTimestamp,
          previousFetched: newestFetchedTimestampRef.current,
        });
        newestFetchedTimestampRef.current = newestItemTimestamp;
        isLoadingItemsRef.current = true; // Set immediately to prevent concurrent calls
        await fetchFeedItemsPage({
          direction: "newer",
          referenceTimestamp: new Date(newestItemTimestamp).toISOString(),
        });
        return; // Exit early to avoid checking for older items in the same call
      } else {
        log.debug("[FETCH ITEMS] checkForMoreItems - newer items already fetched", {
          newestItemTimestamp,
          cachedTimestamp: newestFetchedTimestampRef.current,
        });
      }
    }

    // Check if we need older items
    if (feedMapRef.current?.length > 0 && range[1] >= feedMapRef.current.length - 10) {
      // Expand the local-DB query window first so any older rows already
      // cached in PGlite (from prior visits or eager warmup) become
      // visible immediately, without waiting for an API round-trip.
      if (!windowFullyLoaded) {
        log.debug("[FETCH ITEMS] checkForMoreItems - expanding local window");
        expandFeedWindow();
      }

      if (!isLoadedFirstPageRef.current) {
        const oldestItemTimestamp = feedMapRef.current[feedMapRef.current.length - 1]?.createdAt;
        // Only fetch if this is a new range we haven't fetched before
        if (!oldestFetchedTimestampRef.current || oldestItemTimestamp !== oldestFetchedTimestampRef.current) {
          log.debug("[FETCH ITEMS] checkForMoreItems - fetching older", {
            oldestItemTimestamp,
            previousFetched: oldestFetchedTimestampRef.current,
          });
          oldestFetchedTimestampRef.current = oldestItemTimestamp;
          isLoadingItemsRef.current = true; // Set immediately to prevent concurrent calls
          await fetchFeedItemsPage({
            direction: "older",
            referenceTimestamp: new Date(oldestItemTimestamp).toISOString(),
          });
        } else {
          log.debug("[FETCH ITEMS] checkForMoreItems - older items already fetched", {
            oldestItemTimestamp,
            cachedTimestamp: oldestFetchedTimestampRef.current,
          });
        }
      }
    }
  };

  const scrollToIndex = (index: number) => {
    if (preventScroll) {
      setTimeout(() => setPreventScroll(() => false), 250);
      return;
    }
    vListRef.current?.scrollToIndex(index, { align: "start" });
    setAllowJumpButton(() => true);
  };

  const handleRangeChange = ({ startIndex, endIndex }: { startIndex: number; endIndex: number }) => {
    if (endIndex === feedMapRef.current?.length - 1) {
      setShowScrollToBottom(() => false);
    } else {
      setShowScrollToBottom(
        () => endIndex <= feedMapRef.current?.length - 1 && feedMapRef.current?.length > countThreshold,
      );
    }
    const range = startIndex > 0 ? [startIndex - 5, endIndex + 5] : [0, 10];
    setRange(() => range);
  };

  const findFirstUnreadIndex = feedMapRef.current?.findIndex((feedItem) => feedItem.unread === true);
  const unreadCount = feedMapRef.current?.filter((item) => item.unread)?.length;

  const jumpToLastItem = () => {
    scrollToIndex(feedMapRef.current?.length - 1);
    setPreventScrollAction();
  };

  /**
   * useEffects ➞
   *    0. Initial items fetch on feed change
   *    1. Download Feed Items
   *    2. Scroll to unread when no deeplink
   *    3. Reset UI on feed change (preserve deeplink state when URL has hash/query)
   *    4. Set unread active item
   *    5. Sync active hash from location
   *    6. Scroll to deeplinked item as the map fills
   *    7. Clear URL after highlight dwell (not tied to map churn)
   *    8. Mark unread items / scroll to bottom when no deeplink
   */

  // 0. Initial items fetch when feed changes, when component mounts, or when a
  //    search result navigates to the same feed with a new feedItemId.
  useEffect(() => {
    let cancelled = false;

    log.debug("[FETCH ITEMS] initial load", {
      feedId,
      currentFeedItems: currentFeedItems.length,
      loadedLastPage: currentFeed?.loadedLastPage,
      hasContentId,
    });

    isLoadedFirstPageRef.current = currentFeed?.loadedFirstPage || false;
    isLoadedLastPageRef.current = currentFeed?.loadedLastPage || false;
    initialContentLoad.current = !currentFeedItems.length;
    isLoadingItemsRef.current = false;
    initialItemLoadRef.current = true;
    downloadedContentIds.current = new Set();
    setFetchDoneFeedId(null);
    oldestFetchedTimestampRef.current = null;
    newestFetchedTimestampRef.current = null;

    const init = async () => {
      if (feedId) {
        await resetOrphanedLoadedContent(feedId);
      }
      if (cancelled) return;

      if (hasContentId) {
        setActiveHashOrFeedItemId(hasContentId);
        log.debug("[FETCH ITEMS] deeplink fetch");
        await fetchFeedItemsPage({ direction: "newer", referenceItemId: hasContentId });
        if (cancelled) return;
        await fetchFeedItemsPage({ direction: "older", referenceItemId: hasContentId });
        if (cancelled) return;
        // Anchor the local-DB window at the deeplinked item so it (and
        // everything newer) is guaranteed to be in feedMap when the
        // virtual list scrolls to it. Without this the default newest-page
        // window would hide deeplinks that point to older items.
        try {
          const deeplinkedRow = await db
            .select({ createdAt: itemTable.createdAt })
            .from(itemTable)
            .where(eq(itemTable.id, hasContentId))
            .limit(1);
          const deeplinkedAt = deeplinkedRow[0]?.createdAt;
          if (!cancelled && deeplinkedAt) {
            setFeedWindowStartTimestamp(deeplinkedAt);
          }
        } catch (err) {
          log.warn("[FETCH ITEMS] failed to look up deeplinked item timestamp", { err });
        }
        if (!cancelled) setFetchDoneFeedId(feedId);
      } else if (currentFeed?.loadedLastPage) {
        log.debug("[FETCH ITEMS] skip - feed already fully loaded", {
          itemCount: currentFeedItems.length,
        });
        initialItemLoadRef.current = false;
        setFetchDoneFeedId(feedId);
      } else {
        log.debug("[FETCH ITEMS] fetching items");
        await fetchFeedItemsPage({ direction: "older" });
        if (!cancelled) setFetchDoneFeedId(feedId);
      }
    };
    init();

    return () => {
      cancelled = true;
    };
  }, [feedId, hasContentId]);

  // 1. Download feed items content
  useEffect(() => {
    if (fetchDone) {
      downloadFeedItems();
    }
  }, [preferredLanguage, feedId, range, fetchDone, currentFeedItems.length]);

  // 2. Scroll to unread items when they appear (skip when navigating to a specific item)
  useEffect(() => {
    if (unreadCount > 0 && !activeHashOrFeedItemId) {
      scrollToIndex(feedMapIds?.length - 1);
    }
  }, [unreadCount, feedMapIds?.length]);

  // 3. Reset values
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const feedItemIdFromQuery = searchParams.get("feedItemId");
    const hashFragment = window.location.hash?.split("#")?.[1] ?? null;
    const deeplinkId = hashFragment || feedItemIdFromQuery;

    setActiveFeedItemIndex(() => null);
    setPreventScroll(() => false);
    setShowScrollToBottom(() => false);
    setAllowJumpButton(() => false);
    if (!deeplinkId) {
      setActiveHashOrFeedItemId(() => null);
    }
    setActiveIndex(() => -1);
    initialContentLoad.current = true;
    ampli.feedOpen({ feedId });
  }, [feedId]);

  // 4. Set unread active item
  useEffect(() => {
    setActiveIndex(() => findFirstUnreadIndex);
  }, [feedMapRef.current?.length]);

  // 5. Sync active hash / feedItemId from the location (hash or ?feedItemId=)
  useEffect(() => {
    if (hasContentId) {
      setActiveHashOrFeedItemId(() => hasContentId);
    }
  }, [hasContentId]);

  // 6. Scroll to deeplinked item once it appears in the map (re-run as the window fills).
  useEffect(() => {
    if (!activeHashOrFeedItemId) {
      return;
    }
    const hashId = activeHashOrFeedItemId;
    const scheduledFeedId = feedId;
    const scrollTimer = window.setTimeout(() => {
      const scrollPosition = feedMapRef.current?.findIndex((feedItem) => feedItem.id === hashId);
      if (scrollPosition !== undefined && scrollPosition >= 0) {
        scrollToIndex(scrollPosition);
        setActiveFeedItemIndex(() => `${scheduledFeedId}:${scrollPosition}`);
      }
    }, 250);
    return () => {
      clearTimeout(scrollTimer);
    };
  }, [feedId, activeHashOrFeedItemId, feedMapIds]);

  // 7. Strip the URL and clear highlight after a fixed dwell time (not tied to feedMap churn).
  useEffect(() => {
    if (!activeHashOrFeedItemId) {
      return;
    }
    const hashId = activeHashOrFeedItemId;
    const removeTimer = window.setTimeout(() => {
      removeHash(hashId);
    }, highlightTimeout);
    return () => {
      clearTimeout(removeTimer);
    };
  }, [feedId, activeHashOrFeedItemId]);

  // 8. Mark unread items ➞ Scroll
  useEffect(() => {
    if (!activeHashOrFeedItemId && feedMapRef.current?.length > 0) {
      if (findFirstUnreadIndex > 0) {
        scrollToIndex(findFirstUnreadIndex);
        setActiveIndex(() => findFirstUnreadIndex);
      } else {
        scrollToIndex(feedMapRef.current?.length - 1);
      }
    }
  }, [feedMapRef.current?.length, feedId]);

  const JumpToLatestMessage = () => {
    return (
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          bottom: "1rem",
          left: 0,
          display: "flex",
          zIndex: 300,
        }}
      >
        <Box
          sx={{
            maxWidth: "1300px",
            margin: "0 auto",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Chip
            id="jumpToLatestMessage"
            role="button"
            onClick={() => jumpToLastItem()}
            sx={{
              background: theme.palette.brand.secondary.main,
              "&:hover": { background: theme.palette.brand.secondary.light },
            }}
            label={
              <Box sx={{ display: "flex", alignItem: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <ArrowDownward role="img" sx={{ fontSize: "1rem", mr: 1 }} />
                </Box>
                <Box>Jump to latest message</Box>
              </Box>
            }
          />
        </Box>
      </Box>
    );
  };
  const scrollEndTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const newDesign = flags.newDesign === true;

  const renderChild = (feedItem: any, index: number) => {
    if (!feedItem) {
      return <></>;
    }

    // Defense-in-depth: even if `isFirstDateGroupItem` is computed wrong,
    // never render the same date header twice in a row. A header should only
    // render when the previous rendered item belongs to a different date.
    const previousFeedItem = index > 0 ? feedMapRef.current?.[index - 1] : null;
    const showDateHeader = !!feedItem?.isFirstDateGroupItem && previousFeedItem?.shortDateStr !== feedItem.shortDateStr;

    return (
      <Box
        key={`feed-item-${feedItem?.id}`}
        sx={{
          maxWidth: newDesign ? "none" : "1300px",
          margin: "0 auto",
        }}
      >
        {showDateHeader ? (
          <FeedItemHeader
            isStuck={false}
            dateId={feedItem.shortDateStr}
            onClick={() => {}}
            dateLabel={feedItem.dateGroupLabel}
            isUnreadHeader={false}
            unreadCount={0}
          />
        ) : newDesign ? (
          <></>
        ) : (
          <Box sx={{ height: 34 }} />
        )}

        {activeIndex === index && (
          <Box
            sx={{
              width: "100%",
              textAlign: "right",
              borderBottom: `2px solid ${theme.palette.brand.secondary.main}`,
              fontSize: "12px",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 500,
                color: theme.palette.brand.secondary.main,
              }}
            >
              New
            </Typography>
          </Box>
        )}
        <div
          style={{
            paddingBottom: "16px",
            ...(newDesign && index !== feedMapRef.current.length - 1 ? { marginBottom: "16px" } : {}),
          }}
        >
          <FeedItem
            preventScroll={(_value: boolean) => {
              setPreventScrollAction();
            }}
            setItemNotLoaded={async () => downloadFeedItems()}
            feed={currentFeed}
            item={feedItem}
            vListIndex={index}
            aliasChannelOwnerId={aliasChannelOwnerId}
            active={`${feedId}:${index}` === activeFeedItemIndex}
            highlightPodcast={`${feedId}:${index}` === activeFeedItemIndex}
          />
        </div>
      </Box>
    );
  };

  const footerBox = feedFooterHeight;
  const feedListInset = -10;
  const calculatedHeightNumber = hasGlobalAudioPlayer
    ? footerBox + topHeaderHeight + globalAudioPlayerHeight + feedListInset
    : footerBox + topHeaderHeight + feedListInset;

  const calculatedHeight = `calc(100vh - ${calculatedHeightNumber}px)`;
  const { activeItemId } = useContext(AudioAppContext);
  useEffect(() => {
    if (activeItemId && newDesign) {
      setTimeout(() => {
        const playingFeedItem = document.getElementById(`feed-item-${activeItemId}`);
        if (playingFeedItem) {
          playingFeedItem.scrollIntoView({
            block: "center",
            behavior: "smooth",
          });
        }
      }, 250);
    }
  }, [activeItemId, newDesign]);

  return (
    <Profiler id="FeedVirtualizedList" onRender={onRenderCallback}>
      <Box
        sx={{
          position: "relative",
          height: newDesign ? calculatedHeight : "calc(100svh - 275px)",
          paddingRight: newDesign ? 0 : "6px",
          paddingLeft: newDesign ? 0 : "16px",
          ...(newDesign && footerTransitionSx),
        }}
        aria-label={Locator.feed.items.list}
      >
        {showScrollToBottom && allowJumpButton && <JumpToLatestMessage />}
        <VirtualizedList
          refValue={vListRef}
          id="vListRef"
          shift={false}
          reverse={true}
          data={currentFeedItems}
          itemSize={316}
          childComponent={renderChild}
          emptyState={
            fetchDone ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: theme.palette.primary.main,
                }}
              >
                <Typography variant="h6" fontWeight={700}>
                  There are no messages yet
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                <CircularProgress size={32} />
              </Box>
            )
          }
          onScroll={(value: { startIndex: number; endIndex: number }) => handleRangeChange(value)}
          onScrollEnd={() => {
            if (scrollEndTimeoutRef.current) {
              clearTimeout(scrollEndTimeoutRef.current);
            }
            scrollEndTimeoutRef.current = setTimeout(() => {
              checkForMoreItems().then(() => downloadFeedItems());
            }, 100);
          }}
        />
      </Box>
    </Profiler>
  );
}
